import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ZoneMarket.css';

// Project analysis — the developer's project against its catchment, from myipl market data.
// Totals only: no competitor project names, no brokers, no individual listings.
const n = (v) => (v === null || v === undefined ? 0 : Number(v));
const fmt = (v) => (v ? n(v).toLocaleString('en-IN') : '—');
const inr = (v) => {
  const x = n(v);
  if (!x) return '—';
  if (x >= 1e7) return `₹${(x / 1e7).toFixed(2)} Cr`;
  if (x >= 1e5) return `₹${(x / 1e5).toFixed(1)} L`;
  return `₹${x.toLocaleString('en-IN')}`;
};
const psf = (v) => (v ? `₹${n(v).toLocaleString('en-IN')}` : '—');
const sum = (rows, k) => rows.reduce((a, r) => a + n(r[k]), 0);

const KIND_LABEL = { school: 'Schools', hospital: 'Hospitals', mall: 'Malls', station: 'Railway stations', office_park: 'Office parks' };
const INFRA_LABEL = { metro: 'Metro', expressway: 'Expressway', tunnel: 'Tunnel', bridge: 'Bridge', road: 'Road', monorail: 'Monorail', hsr: 'Bullet train' };

const Box = ({ title, tone, items }) => (
  <div className={`pa-box pa-${tone}`}>
    <div className="pa-box-title">{title}</div>
    {items.length ? (
      <ul>{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
    ) : (
      <div className="pa-empty">Nothing stands out.</div>
    )}
  </div>
);

const ProjectAnalysis = ({ apiBase }) => {
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${apiBase}/zone-market/projects`)
      .then((res) => {
        const list = res.data?.projects || [];
        setProjects(list);
        if (list.length) setPid(list[0].id);
      })
      .catch(() => setError('Could not load your projects.'));
  }, [apiBase]);

  useEffect(() => {
    if (!pid) return;
    let live = true;
    setLoading(true);
    setError(null);
    axios.get(`${apiBase}/zone-market/project/${pid}`)
      .then((res) => { if (live) setData(res.data); })
      .catch((e) => { if (live) { setData(null); setError(e.response?.data?.error || 'Could not build the analysis.'); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [apiBase, pid]);

  if (projects && projects.length === 0) return <div className="zm"><div className="zm-loading">No projects on this account yet.</div></div>;

  const rows = data?.localities || [];
  const ins = data?.insights;
  const f = ins?.figures || {};
  const p = data?.project;
  const units = sum(rows, 'units'), sold = sum(rows, 'sold');
  const infraOp = (data?.infra || []).filter((i) => i.status === 'operational' && i.km <= 3).slice(0, 8);
  const infraSoon = (data?.infra || []).filter((i) => i.status !== 'operational' && i.km <= 5).slice(0, 8);

  return (
    <div className="zm">
      <div className="zm-bar">
        <label htmlFor="pa-proj">Project</label>
        <select id="pa-proj" value={pid || ''} onChange={(e) => setPid(Number(e.target.value))}>
          {(projects || []).map((x) => <option key={x.id} value={x.id}>{x.name}{x.location ? ` — ${x.location}` : ''}</option>)}
        </select>
        {data && <span className="zm-note">Catchment: {rows.length} localities within {data.catchment_km} km · totals only</span>}
      </div>

      {error && <div className="zm-error">{error}</div>}
      {loading && !data && <div className="zm-loading">Building the analysis…</div>}

      {data && ins && (
        <div className={loading ? 'zm-body zm-dim' : 'zm-body'}>
          <div className="zm-tiles">
            <div className="zm-tile">
              <div className="zm-k">Your starting rate</div>
              <div className="zm-v">{psf(f.own_psf)}</div>
              <div className="zm-s">{p?.price_range || (p?.min_price ? `From ${inr(p.min_price)}` : 'per sq ft')}</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Catchment resale ask</div>
              <div className="zm-v">{psf(f.ask_psf)}</div>
              <div className="zm-s">median per sq ft carpet · {fmt(f.for_sale)} for sale</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Registered deals</div>
              <div className="zm-v">{psf(f.deed_psf)}</div>
              <div className="zm-s">median per sq ft{data.deeds_until ? ` · 12 months to ${data.deeds_until}` : ''}</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Active buyers · 90 days</div>
              <div className="zm-v">{fmt(f.buyers)}</div>
              <div className="zm-s">median budget {inr(f.budget)}</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">RERA competition</div>
              <div className="zm-v">{fmt(sum(rows, 'proj_n'))}</div>
              <div className="zm-s">projects · {fmt(units - sold)} units unsold</div>
            </div>
            <div className="zm-tile">
              <div className="zm-k">Nearest metro</div>
              <div className="zm-v">{infraOp.find((i) => i.category === 'metro')?.km ?? '—'} km</div>
              <div className="zm-s">{infraOp.find((i) => i.category === 'metro')?.name || 'none within 3 km'} · airport {data.airport?.km} km</div>
            </div>
          </div>

          <div className="pa-swot">
            <Box title="Strengths" tone="s" items={ins.strengths} />
            <Box title="Weaknesses" tone="w" items={ins.weaknesses} />
            <Box title="Opportunities" tone="o" items={ins.opportunities} />
            <Box title="Threats" tone="t" items={ins.threats} />
          </div>

          <div className="pa-reco">
            <div className="pa-box-title">Recommendations</div>
            <ol>{ins.recommendations.map((t, i) => <li key={i}>{t}</li>)}</ol>
          </div>

          <h3 className="pa-h">Catchment by locality</h3>
          <div className="zm-table-wrap">
            <table className="zm-table">
              <thead>
                <tr>
                  <th>Locality</th><th>Distance</th><th>For sale</th><th>Median ₹/sq ft</th><th>For rent</th>
                  <th>Typical rent</th><th>Buyers (90 d)</th><th>Median budget</th><th>RERA projects</th>
                  <th>Unsold units</th><th>Due in 2 yrs</th><th>Deed ₹/sq ft</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.locality}>
                    <td className="zm-loc">{r.locality}</td>
                    <td>{r.km ? `${r.km} km` : 'here'}</td>
                    <td>{fmt(r.sale_n)}</td>
                    <td>{psf(r.sale_psf)}</td>
                    <td>{fmt(r.rent_n)}</td>
                    <td>{inr(r.rent_med)}</td>
                    <td>{fmt(r.buy_n)}</td>
                    <td>{inr(r.buy_budget)}</td>
                    <td>{fmt(r.proj_n)}</td>
                    <td>{r.units ? fmt(Math.max(n(r.units) - n(r.sold), 0)) : '—'}</td>
                    <td>{fmt(r.poss_2y)}</td>
                    <td>{psf(r.deed_psf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pa-two">
            <div>
              <h3 className="pa-h">Connectivity</h3>
              <div className="pa-list">
                <div className="pa-sub">Operational within 3 km</div>
                {infraOp.length ? infraOp.map((i, k) => (
                  <div key={k} className="pa-row"><span>{INFRA_LABEL[i.category] || i.category} · {i.name}{i.line ? ` (${i.line})` : ''}</span><b>{i.km} km</b></div>
                )) : <div className="pa-empty">None found.</div>}
                <div className="pa-sub">Coming within 5 km</div>
                {infraSoon.length ? infraSoon.map((i, k) => (
                  <div key={k} className="pa-row"><span>{INFRA_LABEL[i.category] || i.category} · {i.name}{i.expected_year ? `, ${i.expected_year}` : ''}</span><b>{i.km} km</b></div>
                )) : <div className="pa-empty">None found.</div>}
                <div className="pa-row"><span>{data.airport?.name}</span><b>{data.airport?.km} km</b></div>
              </div>
            </div>
            <div>
              <h3 className="pa-h">Amenities in the catchment</h3>
              <div className="pa-list">
                {Object.keys(KIND_LABEL).filter((k) => data.places?.[k]).map((k) => (
                  <div key={k} className="pa-amen">
                    <div className="pa-sub">{KIND_LABEL[k]} · {data.places[k].count}</div>
                    <div className="pa-chips">{data.places[k].top.map((x, i) => <span key={i} title={x.locality}>{x.name}</span>)}</div>
                  </div>
                ))}
                {!Object.keys(data.places || {}).length && <div className="pa-empty">None on record.</div>}
              </div>
            </div>
          </div>

          <p className="zm-foot">
            Built automatically from live market data for the localities within {data.catchment_km} km of the project's map pin.
            Listings are live published listings; buyers are client requirements from the last 90 days; RERA figures cover new and ongoing
            registrations and are shown as totals only. Distances are straight-line. Registered-deal figures run to {data.deeds_until || 'the latest available date'}.
            Refreshed every 30 minutes.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectAnalysis;
